import { timestampDate } from "@bufbuild/protobuf/wkt";
import { IconCheckmark2Small } from "central-icons/IconCheckmark2Small";
import { IconColumnWideAdd } from "central-icons/IconColumnWideAdd";
import { IconDossier } from "central-icons/IconDossier";
import { IconHeartBeat } from "central-icons/IconHeartBeat";
import IconPencil from "central-icons/IconPencil";
import { IconSidebarSimpleRightWide } from "central-icons/IconSidebarSimpleRightWide";
import { IconSquareBehindSquare4 } from "central-icons/IconSquareBehindSquare4";
import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type FC } from "react";
import { useNavigate } from "react-router-dom";

import { IconSidebarSimpleRightWideFilled } from "@/assets/icons/central/IconSidebarSimpleRightWideFilled";
import { IconChevronDownSmall } from "@/assets/icons/geist/IconChevronDownSmall";
import { IconFile } from "@/assets/icons/geist/IconFile";
import { IconPanelRightOpen } from "@/assets/icons/geist/IconPanelRightOpen";
import { IconPlay } from "@/assets/icons/geist/IconPlay";
import { IconStop } from "@/assets/icons/geist/IconStop";
import { IconTrash } from "@/assets/icons/geist/IconTrash";
import { IconVSCode } from "@/assets/icons/geist/IconVSCode";
import { IconOnaCopy } from "@/assets/icons/IconOnaCopy";
import { IconOnaDownload } from "@/assets/icons/IconOnaDownload";
import { IconOnaFolder } from "@/assets/icons/IconOnaFolder";
import { IconOnaReset } from "@/assets/icons/IconOnaReset";
import { OnaLettermark } from "@/assets/icons/OnaLettermark";
import type { BaseIconProps } from "@/assets/icons/utils";
import { fromGitpodSize, withGitpodSize } from "@/assets/icons/utils";
import { ClaudeCodeLogo } from "@/assets/logos/ClaudeCodeLogo";
import { CodexLogo } from "@/assets/logos/CodexLogo";
import { browserAnnotationModeAtom, type ActiveLogView } from "@/atoms/ui-state";
import { AgentConfigMenuItems } from "@/components/ai/AgentConfigMenuItems";
import { DeleteAgentExecutionModal } from "@/components/ai/DeleteAgentExecutionModal";
import { AutoStopSubmenu } from "@/components/environments/AutoStopSubmenu";
import {
    EMPTY_ENVIRONMENT,
    IconFileTextSized,
    IconGlobusSized,
    TAB_DRAG_MIME,
} from "@/components/environments/environment-tab-shared";
import { toTimeoutValue } from "@/components/environments/environment-timeout";
import { OpenInEditorButton } from "@/components/environments/OpenInEditorButton";
import { ResourceUsageDialog } from "@/components/environments/ResourceUsageDialog";
import { SuperOnaOpenPortButton } from "@/components/environments/SuperOnaOpenPortButton";
import { Button } from "@/components/flexkit/Button";
import { StatusDot } from "@/components/flexkit/status-dot/StatusDot";
import { NavigationTabs } from "@/components/flexkit/tabs/NavigationTabs";
import { ScrollableTabList } from "@/components/flexkit/tabs/ScrollableTabList";
import { useToast } from "@/components/flexkit/toast";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { cn } from "@/components/podkit/lib/cn";
import { SkeletonText } from "@/components/podkit/loading/Skeleton";
import { Tooltip } from "@/components/Tooltip";
import { timeAgo } from "@/format/time";
import { useAgentExecutionActions } from "@/hooks/use-agent-execution-actions";
import { useCompleteSupportBundle } from "@/hooks/use-complete-support-bundle";
import { useDesktop } from "@/hooks/use-desktop";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { useTemporaryState } from "@/hooks/use-temporary-value";
import { Agents, type PlainAgentExecution } from "@/queries/agent-queries";
import { useGetService, useGetTask } from "@/queries/automations-queries";
import {
    useEnvironment,
    useStartEnvironment,
    useStopEnvironment,
    type PlainEnvironment,
} from "@/queries/environment-queries";
import { getRepoUrl } from "@/repo-url";
import { getConversationDotProps, getEnvironmentDotProps } from "@/routes/environments/conversation-status";
import { DeleteEnvironmentModal } from "@/routes/environments/DeleteEnvironmentModal";
import { showStart, showStop } from "@/routes/environments/phase";
import { agentExecutionName, getConversationTabName } from "@/utils/agent-executions";
import { formatError } from "@/utils/errors";
import { ToggleDetailsPanelKeyBinding, ToggleSplitViewKeyBinding } from "@/utils/platform";
import { isSubagentExecution } from "@/utils/subagent";

const IconHeartBeatSized = withGitpodSize(IconHeartBeat);
const IconPencilSized = withGitpodSize(IconPencil);
const SidebarIconClosed = withGitpodSize(IconSidebarSimpleRightWide);

/** Wraps OnaLettermark (raw SVG) to match the BaseIconProps interface used by NavigationTabs.Trigger. */
export const OnaLettermarkIcon: FC<BaseIconProps> = ({ size, className, ...props }) => {
    const px = fromGitpodSize(size);
    return <OnaLettermark width={px} height={px} className={className} {...props} />;
};

export type TabName =
    | "code"
    | "conversation"
    | "file"
    | "environment"
    | "logs"
    | "browser"
    | `claude-agent-${string}`
    | `codex-agent-${string}`;

/** Wraps a tab trigger to make it draggable between split panes. */
export const DraggableTab: FC<{
    tabValue: TabName;
    enabled: boolean;
    onDragStart?: (tab: TabName) => void;
    children: React.ReactNode;
}> = ({ tabValue, enabled, onDragStart, children }) => {
    const ref = useRef<HTMLDivElement>(null);

    if (!enabled) return <>{children}</>;
    return (
        <div
            ref={ref}
            draggable
            onDragStart={(e: DragEvent) => {
                e.dataTransfer.setData(TAB_DRAG_MIME, tabValue);
                e.dataTransfer.effectAllowed = "move";

                // Create a rounded drag ghost that matches the tab's visual style.
                const source = ref.current;
                if (source) {
                    const ghost = source.cloneNode(true) as HTMLElement;
                    ghost.style.cssText = `
                        position: fixed; top: -1000px; left: -1000px;
                        background: var(--color-surface-02, #f3f4f6);
                        border-radius: 8px; padding: 4px 8px;
                        pointer-events: none;
                    `;
                    document.body.appendChild(ghost);
                    e.dataTransfer.setDragImage(ghost, ghost.offsetWidth / 2, ghost.offsetHeight / 2);
                    // Clean up after the browser captures the image.
                    requestAnimationFrame(() => ghost.remove());
                }

                onDragStart?.(tabValue);
            }}
        >
            {children}
        </div>
    );
};

export const SuperOnaConversationHeader: FC<{
    environmentId?: string;
    agentExecution?: PlainAgentExecution | null;
    isDetailsPanelOpen: boolean;
    onToggleDetailsPanel: () => void;
    activeTab?: TabName;
    onTabChange?: (tab: TabName) => void;
    /** File path currently open in the diff tab */
    selectedFilePath?: string | null;
    /** Close the file tab */
    onCloseFileTab?: () => void;
    /** Whether the environment tab is open (persists across tab switches until dismissed) */
    isEnvironmentTabOpen?: boolean;
    /** Dismiss the environment tab */
    onCloseEnvironmentTab?: () => void;
    /** Active automation log view (service or task run) */
    activeLogView?: ActiveLogView | null;
    /** Close the logs tab */
    onCloseLogsTab?: () => void;
    /** All agent executions for this environment, including subagents (for execution tabs) */
    allExecutions?: PlainAgentExecution[];
    /** Switch to a different execution */
    onSelectExecution?: (executionId: string) => void;
    /** Subagent execution IDs that have been opened as tabs */
    openSubagentTabs?: Set<string>;
    /** Close a subagent tab */
    onCloseSubagentTab?: (executionId: string) => void;
    /** Whether the browser VNC port is available */
    hasBrowserPort?: boolean;
    /** Claude agent executions — each gets a tab */
    claudeAgentExecutions?: PlainAgentExecution[];
    /** Codex agent executions — each gets a tab */
    codexAgentExecutions?: PlainAgentExecution[];
    /** Whether supervisor-dependent features (file diff, terminal, Claude Agent) are available */
    supervisorFeaturesEnabled?: boolean;
    /** Whether AI/agents are enabled for this org (distinct from whether an execution exists yet). */
    isAIEnabled?: boolean;
    /** Whether the content area is currently split. */
    isSplit?: boolean;
    /** Toggle split view. */
    onToggleSplit?: () => void;
    /** Called when a tab starts being dragged (for split-pane reordering). */
    onTabDragStart?: (tab: TabName) => void;
}> = ({
    environmentId,
    agentExecution,
    isDetailsPanelOpen,
    onToggleDetailsPanel,
    activeTab: controlledTab,
    onTabChange,
    selectedFilePath,
    onCloseFileTab,
    isEnvironmentTabOpen,
    onCloseEnvironmentTab,
    activeLogView,
    onCloseLogsTab,
    allExecutions,
    onSelectExecution,
    openSubagentTabs,
    onCloseSubagentTab,
    hasBrowserPort,
    claudeAgentExecutions,
    codexAgentExecutions,
    supervisorFeaturesEnabled = true,
    isAIEnabled = true,
    isSplit = false,
    onToggleSplit,
    onTabDragStart,
}) => {
    const { data: environment } = useEnvironment(environmentId);
    const { isMobileViewport: isMobile } = useIsMobileViewport();
    const { isDesktop } = useDesktop();
    const [internalTab, setInternalTab] = useState<TabName>("conversation");
    const activeTab = controlledTab ?? internalTab;
    const setActiveTab = onTabChange ?? setInternalTab;
    const [showResetModal, setShowResetModal] = useState(false);
    const [annotationMode, setAnnotationMode] = useAtom(browserAnnotationModeAtom);

    // Reset modal state when the agent execution changes (e.g., after a reset
    // completes and a new execution is created). Guards against the onClose
    // callback not firing due to the modal unmounting before the mutation's
    // onSuccess runs.
    useEffect(() => {
        setShowResetModal(false);
    }, [agentExecution?.id]);

    // Turn off annotation mode when leaving the browser tab.
    useEffect(() => {
        if (activeTab !== "browser" && annotationMode) {
            setAnnotationMode(false);
        }
    }, [activeTab, annotationMode, setAnnotationMode]);

    const { copyAgentExecutionId } = useAgentExecutionActions(agentExecution ?? undefined);
    const { downloadSupportBundle, isSupportBundleAvailable, enableTokenFetch } = useCompleteSupportBundle(
        environment ?? EMPTY_ENVIRONMENT,
        allExecutions,
    );

    // Eagerly enable token fetch — the context menu is always available.
    useEffect(() => {
        enableTokenFetch();
    }, [enableTokenFetch]);

    const conversationName = agentExecution ? agentExecutionName(agentExecution) : "Conversation";

    // Resolve the parent execution name for the single conversation tab.
    const parentExecution = useMemo(() => allExecutions?.find((exec) => !isSubagentExecution(exec)), [allExecutions]);
    const parentConversationName = getConversationTabName(environment?.metadata?.name, parentExecution, agentExecution);

    // Pick the conversation tab icon based on the primary agent.
    const conversationTabIcon = useMemo(() => {
        const agentId = agentExecution?.spec?.agentId;
        if (agentId === Agents.InEnvironmentClaudeAgent.id) return ClaudeCodeLogo;
        if (agentId === Agents.InEnvironmentCodexAgent.id) return CodexLogo;
        if (agentId === Agents.InEnvironmentCodexAppAgent.id) return CodexLogo;
        return OnaLettermarkIcon;
    }, [agentExecution?.spec?.agentId]);

    // Open subagent executions that should render as tabs.
    const openSubagentExecutions = useMemo(
        () => (openSubagentTabs && allExecutions ? allExecutions.filter((exec) => openSubagentTabs.has(exec.id)) : []),
        [openSubagentTabs, allExecutions],
    );
    // Track which execution tab is selected locally so the Radix value
    // updates synchronously on click, before the agentExecution prop
    // catches up from the parent.
    const [selectedExecId, setSelectedExecId] = useState(agentExecution?.id);
    useEffect(() => {
        setSelectedExecId(agentExecution?.id);
    }, [agentExecution?.id]);

    const renderContextMenu = (trackingSuffix: string) =>
        agentExecution ? (
            <>
                <DropdownMenuItem
                    onClick={copyAgentExecutionId}
                    LeadingIcon={IconOnaCopy}
                    data-tracking-id={`copy-session-id${trackingSuffix}`}
                >
                    Copy conversation ID
                </DropdownMenuItem>
                <Tooltip content={!isSupportBundleAvailable ? "Support bundle is temporarily unavailable" : undefined}>
                    <div>
                        <DropdownMenuItem
                            onClick={downloadSupportBundle}
                            disabled={!isSupportBundleAvailable}
                            LeadingIcon={IconOnaDownload}
                            data-tracking-id={`download-support-bundle${trackingSuffix}`}
                        >
                            Download support bundle
                        </DropdownMenuItem>
                    </div>
                </Tooltip>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                    onClick={() => setShowResetModal(true)}
                    variant="destructive"
                    LeadingIcon={IconOnaReset}
                    data-tracking-id={`reset-session${trackingSuffix}`}
                >
                    Reset conversation
                </DropdownMenuItem>
            </>
        ) : undefined;
    const branch = environment?.status?.content?.git?.branch;
    const repoInfo = environment ? getRepoUrl(environment) : undefined;
    const branchDisplayName =
        branch || (repoInfo ? `${repoInfo.account}/${repoInfo.repo}` : undefined) || conversationName;
    const handleOpenEnvironmentDetails = useCallback(() => {
        setActiveTab("environment");
    }, [setActiveTab]);

    const claudeTerminalExecutions = supervisorFeaturesEnabled ? (claudeAgentExecutions ?? []) : [];
    const codexTerminalExecutions = codexAgentExecutions ?? [];

    const hasDynamicTabs =
        !!selectedFilePath ||
        !!(isEnvironmentTabOpen && environment) ||
        !!activeLogView ||
        !!hasBrowserPort ||
        claudeTerminalExecutions.length > 0 ||
        codexTerminalExecutions.length > 0;

    return (
        <div className="flex flex-col">
            {/* Primary header row — environment identity + controls (hidden on mobile; SuperOnaMobileHeader provides this) */}
            <div
                className={cn(
                    "flex h-12 w-full items-center justify-between gap-2 border-b border-border-subtle px-4 py-2",
                    isMobile && "hidden",
                )}
            >
                <div className="flex h-6 min-w-0 flex-1 items-center gap-0.5">
                    <SkeletonText ready={!!environment} className="w-40" size="lg">
                        {environment && (
                            <EnvironmentHeaderDropdown
                                environment={environment}
                                agentExecution={agentExecution ?? undefined}
                                displayName={branchDisplayName}
                                onOpenEnvironmentDetails={isAIEnabled ? handleOpenEnvironmentDetails : undefined}
                                allExecutions={allExecutions}
                            />
                        )}
                    </SkeletonText>
                </div>
                <div className="flex items-center justify-end gap-1">
                    {environment && <SuperOnaOpenPortButton environment={environment} />}
                    {environment && !window.isWebview && <OpenInEditorButton environment={environment} />}
                    {isDesktop ? null : isMobile ? (
                        <Tooltip content="Show environment details">
                            <Button
                                variant="ghost"
                                className="rounded-[4px] border-0 p-0"
                                size="lg"
                                onClick={onToggleDetailsPanel}
                                LeadingIcon={IconPanelRightOpen}
                                aria-label="Show environment details"
                                data-tracking-id="show-environment-details-super-ona-header"
                            />
                        </Tooltip>
                    ) : (
                        <Tooltip
                            usePortal
                            content={
                                <div className="flex flex-row items-center gap-2">
                                    <span>{isDetailsPanelOpen ? "Hide details panel" : "Show details panel"}</span>
                                    <KeyboardCombo keys={ToggleDetailsPanelKeyBinding} />
                                </div>
                            }
                        >
                            <Button
                                variant="ghost"
                                className={cn("group relative rounded-[4px] border-0 p-2")}
                                size="sm"
                                onClick={onToggleDetailsPanel}
                                aria-label={isDetailsPanelOpen ? "Hide right panel" : "Show right panel"}
                                data-tracking-id="toggle-details-panel-super-ona-header"
                            >
                                {isDetailsPanelOpen ? (
                                    <>
                                        <IconSidebarSimpleRightWideFilled
                                            size="sm"
                                            className={cn(
                                                "absolute transition-opacity duration-300",
                                                "opacity-100 group-hover:opacity-0",
                                            )}
                                        />
                                        <SidebarIconClosed
                                            size="sm"
                                            className={cn(
                                                "transition-opacity duration-300",
                                                "opacity-0 group-hover:opacity-100",
                                            )}
                                        />
                                    </>
                                ) : (
                                    <SidebarIconClosed size="sm" />
                                )}
                            </Button>
                        </Tooltip>
                    )}
                </div>
            </div>

            {/* Tabs sub-header row — hidden when split view is active (each pane has its own tab bar) */}
            {!isSplit && (
                <NavigationTabs
                    value={
                        activeTab === "conversation" && selectedExecId && openSubagentTabs?.has(selectedExecId)
                            ? `exec:${selectedExecId}`
                            : activeTab
                    }
                    onValueChange={(v) => {
                        // Intercept execution tab clicks — switch to that execution
                        // instead of changing the content tab.
                        if (v.startsWith("exec:")) {
                            const executionId = v.slice("exec:".length);
                            setSelectedExecId(executionId);
                            onSelectExecution?.(executionId);
                            setActiveTab("conversation");
                            return;
                        }
                        // Clicking the parent conversation tab switches back to it.
                        if (v === "conversation" && parentExecution && selectedExecId !== parentExecution.id) {
                            setSelectedExecId(parentExecution.id);
                            onSelectExecution?.(parentExecution.id);
                        }
                        setActiveTab(v as TabName);
                    }}
                >
                    <div className="flex">
                        <div className="min-w-0 flex-1">
                            <ScrollableTabList className="h-12 px-4" borderClassName="border-border-subtle">
                                {/* Static tabs — Code tab hidden on mobile (SuperOnaMobileHeader provides the toggle) and in VS Code webview */}
                                {!isMobile && !window.isWebview && (
                                    <DraggableTab tabValue="code" enabled={isSplit} onDragStart={onTabDragStart}>
                                        <NavigationTabs.Trigger
                                            value="code"
                                            LeadingIcon={IconVSCode}
                                            className="shrink-0"
                                        >
                                            Code
                                        </NavigationTabs.Trigger>
                                    </DraggableTab>
                                )}
                                {/* Conversation tab — disabled with upsell tooltip when AI is
                                    not enabled for the org (environment-only mode). When AI is enabled
                                    but no execution exists yet, the tab stays clickable. */}
                                {isAIEnabled ? (
                                    <DraggableTab
                                        tabValue="conversation"
                                        enabled={isSplit}
                                        onDragStart={onTabDragStart}
                                    >
                                        <NavigationTabs.Trigger
                                            value="conversation"
                                            LeadingIcon={conversationTabIcon}
                                            contextMenuContent={renderContextMenu("-tab")}
                                        >
                                            {parentConversationName}
                                        </NavigationTabs.Trigger>
                                    </DraggableTab>
                                ) : (
                                    <Tooltip
                                        content={
                                            <span>
                                                Ask your admin to enable Agents for your organization.{" "}
                                                <a
                                                    href="https://ona.com/docs/ona/agents#getting-started-with-agents"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="underline"
                                                >
                                                    Learn more
                                                </a>
                                            </span>
                                        }
                                    >
                                        <span className="flex cursor-default items-center opacity-50">
                                            <NavigationTabs.Trigger
                                                value="conversation"
                                                LeadingIcon={conversationTabIcon}
                                                disabled
                                                className="pointer-events-none"
                                            >
                                                Conversation
                                            </NavigationTabs.Trigger>
                                        </span>
                                    </Tooltip>
                                )}
                                {/* Dynamically opened subagent tabs */}
                                {openSubagentExecutions.map((exec) => (
                                    <NavigationTabs.Trigger
                                        key={exec.id}
                                        value={`exec:${exec.id}`}
                                        LeadingIcon={OnaLettermarkIcon}
                                        onClose={() => onCloseSubagentTab?.(exec.id)}
                                    >
                                        {agentExecutionName(exec)}
                                    </NavigationTabs.Trigger>
                                ))}

                                {claudeTerminalExecutions.map((exec) => (
                                    <DraggableTab
                                        key={exec.id}
                                        tabValue={`claude-agent-${exec.id}`}
                                        enabled={isSplit}
                                        onDragStart={onTabDragStart}
                                    >
                                        <NavigationTabs.Trigger
                                            value={`claude-agent-${exec.id}`}
                                            LeadingIcon={ClaudeCodeLogo}
                                        >
                                            Claude Code
                                        </NavigationTabs.Trigger>
                                    </DraggableTab>
                                ))}

                                {codexTerminalExecutions.map((exec) => (
                                    <DraggableTab
                                        key={exec.id}
                                        tabValue={`codex-agent-${exec.id}`}
                                        enabled={isSplit}
                                        onDragStart={onTabDragStart}
                                    >
                                        <NavigationTabs.Trigger
                                            value={`codex-agent-${exec.id}`}
                                            LeadingIcon={CodexLogo}
                                        >
                                            Codex
                                        </NavigationTabs.Trigger>
                                    </DraggableTab>
                                ))}

                                {/* Separator between static and dynamic tabs — only when agent tabs are present */}
                                {isAIEnabled && hasDynamicTabs && <NavigationTabs.Separator />}

                                {/* Dynamic tabs — appear/disappear based on user actions */}
                                {isEnvironmentTabOpen && environment && (
                                    <DraggableTab tabValue="environment" enabled={isSplit} onDragStart={onTabDragStart}>
                                        <NavigationTabs.Trigger
                                            value="environment"
                                            LeadingIcon={(props) => {
                                                // Environment-only dot: the tab indicator must reflect
                                                // environment phase only (not agent activity). See CORE-8507.
                                                const dotProps = getEnvironmentDotProps(environment);
                                                return (
                                                    <StatusDot
                                                        size="sm"
                                                        color={dotProps.color}
                                                        variant={dotProps.variant}
                                                        animation={dotProps.animation}
                                                        className={props.className}
                                                    />
                                                );
                                            }}
                                            onClose={isAIEnabled ? () => onCloseEnvironmentTab?.() : undefined}
                                        >
                                            Environment
                                        </NavigationTabs.Trigger>
                                    </DraggableTab>
                                )}
                                {activeLogView && (
                                    <DraggableTab tabValue="logs" enabled={isSplit} onDragStart={onTabDragStart}>
                                        <NavigationTabs.Trigger
                                            value="logs"
                                            LeadingIcon={IconFileTextSized}
                                            onClose={() => onCloseLogsTab?.()}
                                        >
                                            <LogsTabLabel logView={activeLogView} />
                                        </NavigationTabs.Trigger>
                                    </DraggableTab>
                                )}
                                {selectedFilePath && (
                                    <DraggableTab tabValue="file" enabled={isSplit} onDragStart={onTabDragStart}>
                                        <NavigationTabs.Trigger
                                            value="file"
                                            LeadingIcon={IconFile}
                                            onClose={() => onCloseFileTab?.()}
                                        >
                                            {selectedFilePath.split("/").pop() ?? "File"}
                                        </NavigationTabs.Trigger>
                                    </DraggableTab>
                                )}
                                {hasBrowserPort && (
                                    <DraggableTab tabValue="browser" enabled={isSplit} onDragStart={onTabDragStart}>
                                        <NavigationTabs.Trigger value="browser" LeadingIcon={IconGlobusSized}>
                                            Browser
                                        </NavigationTabs.Trigger>
                                    </DraggableTab>
                                )}
                                {/* Annotation toggle — visible when the browser tab is active */}
                                {activeTab === "browser" && hasBrowserPort && (
                                    <>
                                        <NavigationTabs.Separator />
                                        <Tooltip content={annotationMode ? "Exit annotation mode" : "Annotate browser"}>
                                            <button
                                                type="button"
                                                onClick={() => setAnnotationMode((prev) => !prev)}
                                                data-tracking-id="browser-annotation-toggle"
                                                className={cn(
                                                    "flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-sm font-medium transition-colors",
                                                    annotationMode
                                                        ? "bg-surface-primary-inverted text-content-primary-inverted"
                                                        : "text-content-secondary hover:bg-surface-02 hover:text-content-primary",
                                                )}
                                                aria-label={
                                                    annotationMode ? "Exit annotation mode" : "Annotate browser"
                                                }
                                                aria-pressed={annotationMode}
                                            >
                                                <IconPencilSized size="sm" />
                                            </button>
                                        </Tooltip>
                                    </>
                                )}
                            </ScrollableTabList>
                        </div>
                        {/* Split toggle — pinned to far right, aligned with sidebar toggle above */}
                        {!isMobile && isAIEnabled && onToggleSplit && (
                            <div className="flex shrink-0 items-center border-b border-border-subtle px-4">
                                <Tooltip
                                    usePortal
                                    content={
                                        <div className="flex flex-row items-center gap-2">
                                            <span>{isSplit ? "Close split view" : "Split view"}</span>
                                            <KeyboardCombo keys={ToggleSplitViewKeyBinding} />
                                        </div>
                                    }
                                >
                                    <Button
                                        variant="ghost"
                                        className="rounded-[4px] border-0 p-2"
                                        size="sm"
                                        onClick={onToggleSplit}
                                        aria-label={isSplit ? "Close split view" : "Split view"}
                                        aria-pressed={isSplit}
                                        data-tracking-id="split-view-toggle"
                                    >
                                        {isSplit ? <IconDossier size={16} /> : <IconColumnWideAdd size={16} />}
                                    </Button>
                                </Tooltip>
                            </div>
                        )}
                    </div>
                </NavigationTabs>
            )}

            {agentExecution && (
                <DeleteAgentExecutionModal
                    agentExecution={agentExecution}
                    open={showResetModal}
                    onClose={() => setShowResetModal(false)}
                />
            )}
        </div>
    );
};

/** Resolves the automation name for the logs tab label from the active log view. */
export const LogsTabLabel: FC<{ logView: ActiveLogView }> = ({ logView }) => {
    const isService = logView.type === "service";
    const { data: service } = useGetService(isService ? logView.serviceId : undefined);
    const { data: task } = useGetTask(!isService ? logView.taskId : undefined);
    const name = isService ? service?.metadata?.name : task?.metadata?.name;

    if (name) {
        return <>Logs: {name}</>;
    }
    return <>{isService ? "Service Logs" : "Task Logs"}</>;
};

/** Splits a branch name at the first slash, rendering prefix in secondary color and the rest in primary.
 *  @param bold — when true (default) the rest portion is rendered in bold (used in the trigger). */
export const BranchName: FC<{ name?: string; bold?: boolean; className?: string }> = ({
    name,
    bold = true,
    className,
}) => {
    if (!name) return null;

    const slashIndex = name.indexOf("/");
    if (slashIndex === -1) {
        return (
            <span className={cn("min-w-0 truncate text-left text-content-primary", bold && "font-bold", className)}>
                {name}
            </span>
        );
    }

    const prefix = name.slice(0, slashIndex);
    const rest = name.slice(slashIndex + 1);

    return (
        <span className={cn("flex min-w-0 items-baseline gap-1 truncate text-left", className)}>
            <span className="shrink-0 text-content-secondary">{prefix}</span>
            <span className="shrink-0 text-content-secondary">/</span>
            <span className={cn("min-w-0 truncate text-content-primary", bold && "font-bold")}>{rest}</span>
        </span>
    );
};

export const EnvironmentHeaderDropdown: FC<{
    environment: PlainEnvironment;
    agentExecution?: PlainAgentExecution;
    displayName?: string;
    onOpenEnvironmentDetails?: () => void;
    allExecutions?: PlainAgentExecution[];
}> = ({ environment, agentExecution, displayName, onOpenEnvironmentDetails, allExecutions }) => {
    const dotProps = getConversationDotProps(environment, agentExecution);
    const [isOpen, setIsOpen] = useState(false);
    const [showResourceUsage, setShowResourceUsage] = useState(false);
    const [showDeleteSessionModal, setShowDeleteSessionModal] = useState(false);
    const { toast } = useToast();
    const navigate = useNavigate();
    const startEnvironment = useStartEnvironment();
    const stopEnvironment = useStopEnvironment();
    const { downloadSupportBundle, isSupportBundleAvailable, enableTokenFetch, isTokenReady } =
        useCompleteSupportBundle(environment, allExecutions);

    const canOpenProjectSettings = !!environment.metadata?.projectId;

    const startedTimeAgo = useMemo(() => {
        const createdAt = environment.metadata?.createdAt;
        if (!createdAt) return undefined;
        const date = timestampDate(createdAt);
        return `Started ${timeAgo(date)}`;
    }, [environment.metadata?.createdAt]);

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (open) {
                enableTokenFetch();
            }
            setIsOpen(open);
        },
        [enableTokenFetch],
    );

    const handleOpenProjectSettings = useCallback(() => {
        const projectId = environment.metadata?.projectId;
        if (projectId) {
            navigate(`/projects/${projectId}/settings`);
        }
    }, [environment.metadata?.projectId, navigate]);

    const branch = environment.status?.content?.git?.branch;
    const [branchCopied, setBranchCopied] = useTemporaryState(false, 2000);

    const handleCopyBranch = useCallback(async () => {
        if (!branch) return;
        try {
            await navigator.clipboard.writeText(branch);
            setBranchCopied(true);
            toast({
                title: "Branch name copied to clipboard",
                description: branch,
            });
        } catch {
            // clipboard may be unavailable
        }
    }, [branch, setBranchCopied, toast]);

    const handleCopyId = useCallback(async () => {
        await navigator.clipboard.writeText(environment.id);
        toast({
            title: "Environment ID copied to clipboard",
            description: environment.id,
        });
    }, [toast, environment.id]);

    const handleStart = useCallback(async () => {
        try {
            await startEnvironment.mutateAsync({ environmentId: environment.id });
        } catch (error) {
            toast({
                title: "Failed to start environment",
                description: formatError(error),
            });
        }
    }, [startEnvironment, environment.id, toast]);

    const handleStop = useCallback(() => {
        stopEnvironment.mutate(environment.id, {
            onError: (e) => {
                toast({
                    title: "Failed to stop environment",
                    description: formatError(e),
                });
            },
        });
    }, [stopEnvironment, toast, environment.id]);

    return (
        <>
            <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
                <DropdownMenuTrigger
                    data-testid="environment-header-dropdown-trigger"
                    className="group flex min-w-0 cursor-pointer items-center focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-0 focus-visible:outline-content-primary"
                    aria-label="Environment actions"
                >
                    <div
                        className={cn(
                            "flex min-w-0 items-center gap-1 self-stretch rounded-l-md p-1 transition-colors group-hover:bg-surface-hover group-data-[state=open]:bg-surface-hover",
                        )}
                    >
                        <StatusDot
                            size="sm"
                            color={dotProps.color}
                            variant={dotProps.variant}
                            animation={dotProps.animation}
                            tooltip={dotProps.tooltip}
                            className="shrink-0"
                        />
                        <BranchName name={displayName} className="text-base" />
                    </div>
                    <div
                        className={cn(
                            "flex flex-shrink-0 items-center self-stretch rounded-r-md px-1 transition-colors group-hover:bg-surface-hover group-data-[state=open]:bg-surface-hover",
                        )}
                    >
                        <IconChevronDownSmall size="sm" className="size-4" />
                    </div>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="start" className="w-64">
                    {/* Header section: status dot + branch name + copy + started time */}
                    <DropdownMenuLabel className="flex items-center gap-1.5 px-3 py-2">
                        <StatusDot
                            size="sm"
                            color={dotProps.color}
                            variant={dotProps.variant}
                            animation={dotProps.animation}
                            className="mt-1 shrink-0 self-start"
                        />
                        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                            <BranchName name={displayName} bold={false} className="text-base font-normal" />
                            {startedTimeAgo && (
                                <span className="text-sm font-normal text-content-secondary">{startedTimeAgo}</span>
                            )}
                        </div>
                        {branch && (
                            <button
                                type="button"
                                className="shrink-0 self-center rounded p-1 text-content-strong hover:text-content-primary"
                                onClick={handleCopyBranch}
                                aria-label="Copy branch name"
                                data-tracking-id="copy-branch-name-super-ona-header"
                            >
                                <span className="relative inline-flex size-4 items-center justify-center">
                                    <IconSquareBehindSquare4
                                        size={16}
                                        className={cn(
                                            "absolute inset-0 transition-all duration-150 ease-in-out motion-reduce:transition-none",
                                            branchCopied ? "scale-90 opacity-0" : "scale-100 opacity-100",
                                        )}
                                    />
                                    <IconCheckmark2Small
                                        size={16}
                                        className={cn(
                                            "absolute inset-0 text-content-success transition-all duration-150 ease-in-out motion-reduce:transition-none",
                                            branchCopied ? "scale-100 opacity-100" : "scale-90 opacity-0",
                                        )}
                                    />
                                </span>
                            </button>
                        )}
                    </DropdownMenuLabel>

                    <DropdownMenuSeparator />

                    {/* Group 1: Environment actions */}
                    {onOpenEnvironmentDetails && (
                        <DropdownMenuItem
                            onClick={onOpenEnvironmentDetails}
                            LeadingIcon={IconFileTextSized}
                            data-testid="menu-item-environment-details"
                            data-tracking-id="environment-details-super-ona-header"
                        >
                            Environment details
                        </DropdownMenuItem>
                    )}

                    <DropdownMenuItem
                        onClick={handleCopyId}
                        LeadingIcon={IconOnaCopy}
                        data-tracking-id="copy-environment-id-super-ona-header"
                    >
                        Copy environment ID
                    </DropdownMenuItem>

                    <DropdownMenuItem
                        onClick={() => setShowResourceUsage(true)}
                        LeadingIcon={IconHeartBeatSized}
                        data-testid="menu-item-resource-usage"
                        data-tracking-id="resource-usage-super-ona-header"
                    >
                        Resource usage
                    </DropdownMenuItem>

                    <AutoStopSubmenu
                        environmentId={environment.id}
                        timeoutSeconds={toTimeoutValue(environment.spec?.timeout?.disconnected)}
                        trackingIdSuffix="super-ona-header"
                    />

                    {showStart(environment) && (
                        <DropdownMenuItem
                            onClick={handleStart}
                            LeadingIcon={(props) => (
                                <IconPlay {...props} className={cn(props.className, "text-content-secondary")} />
                            )}
                            data-tracking-id="start-environment-super-ona-header"
                        >
                            Start environment
                        </DropdownMenuItem>
                    )}
                    {showStop(environment) && (
                        <DropdownMenuItem
                            onClick={handleStop}
                            LeadingIcon={(props) => (
                                <IconStop {...props} className={cn(props.className, "text-content-secondary")} />
                            )}
                            data-tracking-id="stop-environment-super-ona-header"
                        >
                            Stop environment
                        </DropdownMenuItem>
                    )}

                    <DropdownMenuSeparator />

                    {/* Group 2: Project & support */}
                    <Tooltip
                        content={!canOpenProjectSettings ? "No project associated with this environment" : undefined}
                    >
                        <div>
                            <DropdownMenuItem
                                onClick={handleOpenProjectSettings}
                                disabled={!canOpenProjectSettings}
                                LeadingIcon={IconOnaFolder}
                                data-tracking-id="go-to-project-super-ona-header"
                            >
                                Go to project
                            </DropdownMenuItem>
                        </div>
                    </Tooltip>

                    {isSupportBundleAvailable && (
                        <DropdownMenuItem
                            onClick={downloadSupportBundle}
                            disabled={!isTokenReady}
                            LeadingIcon={IconOnaDownload}
                            data-tracking-id="download-support-bundle-super-ona-header"
                        >
                            Download support bundle
                        </DropdownMenuItem>
                    )}

                    {/* Agent / dev runner config */}
                    <AgentConfigMenuItems environmentId={environment.id} />

                    {/* Group 3: Destructive actions */}
                    {agentExecution && (
                        <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                variant="destructive"
                                onClick={() => setShowDeleteSessionModal(true)}
                                LeadingIcon={IconTrash}
                                data-tracking-id="delete-session-super-ona-header"
                            >
                                Delete environment
                            </DropdownMenuItem>
                        </>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>

            <ResourceUsageDialog
                environmentId={environment.id}
                open={showResourceUsage}
                onOpenChange={setShowResourceUsage}
            />

            <DeleteEnvironmentModal
                environment={environment}
                agentExecution={agentExecution}
                open={showDeleteSessionModal}
                onClose={() => setShowDeleteSessionModal(false)}
            />
        </>
    );
};
